'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import type {
  PlanType,
  DynamicPricingResponse,
  Currency,
  BillingCycle,
} from '@query/page-query/pricing-query/pricing.model'

/**
 * Context value for sharing pricing state across components
 */
type PricingContextValue = {
  /** The currently selected/highlighted plan type based on user's spend input */
  currentPlanType: PlanType | null
  /** The dynamic pricing response from the API for USD Annual */
  dynamicPricingUSDAnnual: DynamicPricingResponse | null
  /** The dynamic pricing response from the API for USD Monthly */
  dynamicPricingUSDMonthly: DynamicPricingResponse | null
  /** The dynamic pricing response from the API for INR Annual */
  dynamicPricingINRAnnual: DynamicPricingResponse | null
  /** The dynamic pricing response from the API for INR Monthly */
  dynamicPricingINRMonthly: DynamicPricingResponse | null
  /** Currently selected currency */
  currency: Currency
  /** Whether the pricing data is being loaded */
  isLoading: boolean
  /** Update the current plan type (called when dynamic pricing API responds) */
  setCurrentPlanType: (planType: PlanType | null) => void
  /** Set the dynamic pricing response for USD Annual */
  setDynamicPricingUSDAnnual: (pricing: DynamicPricingResponse | null) => void
  /** Set the dynamic pricing response for USD Monthly */
  setDynamicPricingUSDMonthly: (pricing: DynamicPricingResponse | null) => void
  /** Set the dynamic pricing response for INR Annual */
  setDynamicPricingINRAnnual: (pricing: DynamicPricingResponse | null) => void
  /** Set the dynamic pricing response for INR Monthly */
  setDynamicPricingINRMonthly: (pricing: DynamicPricingResponse | null) => void
  /** Set the currency */
  setCurrency: (currency: Currency) => void
  /** Set the loading state */
  setIsLoading: (loading: boolean) => void
  /** Get the current dynamic pricing based on selected currency and billing cycle */
  getCurrentPricing: (
    billingCycle?: BillingCycle
  ) => DynamicPricingResponse | null
}

const PricingContext = createContext<PricingContextValue | undefined>(undefined)

type PricingProviderProps = {
  children: ReactNode
  /** Initial plan type (optional) */
  initialPlanType?: PlanType | null
}

/**
 * Provider component to share pricing state across the pricing page
 *
 * This context allows the PricingCard component to communicate the
 * current plan type and pricing (from dynamic pricing API) to OurPlans component
 * for highlighting the appropriate plan card and displaying the correct price.
 *
 * @example
 * ```tsx
 * <PricingProvider>
 *   <PricingCard />
 *   <OurPlans />
 * </PricingProvider>
 * ```
 */
export function PricingProvider({
  children,
  initialPlanType = null,
}: PricingProviderProps) {
  const [currentPlanType, setCurrentPlanType] = useState<PlanType | null>(
    initialPlanType
  )
  const [dynamicPricingUSDAnnual, setDynamicPricingUSDAnnual] =
    useState<DynamicPricingResponse | null>(null)
  const [dynamicPricingUSDMonthly, setDynamicPricingUSDMonthly] =
    useState<DynamicPricingResponse | null>(null)
  const [dynamicPricingINRAnnual, setDynamicPricingINRAnnual] =
    useState<DynamicPricingResponse | null>(null)
  const [dynamicPricingINRMonthly, setDynamicPricingINRMonthly] =
    useState<DynamicPricingResponse | null>(null)
  const [currency, setCurrency] = useState<Currency>('USD')
  const [isLoading, setIsLoading] = useState(false)

  const handleSetPlanType = useCallback((planType: PlanType | null) => {
    setCurrentPlanType(planType)
  }, [])

  const handleSetDynamicPricingUSDAnnual = useCallback(
    (pricing: DynamicPricingResponse | null) => {
      setDynamicPricingUSDAnnual(pricing)
    },
    []
  )

  const handleSetDynamicPricingUSDMonthly = useCallback(
    (pricing: DynamicPricingResponse | null) => {
      setDynamicPricingUSDMonthly(pricing)
    },
    []
  )

  const handleSetDynamicPricingINRAnnual = useCallback(
    (pricing: DynamicPricingResponse | null) => {
      setDynamicPricingINRAnnual(pricing)
    },
    []
  )

  const handleSetDynamicPricingINRMonthly = useCallback(
    (pricing: DynamicPricingResponse | null) => {
      setDynamicPricingINRMonthly(pricing)
    },
    []
  )

  const handleSetCurrency = useCallback((curr: Currency) => {
    setCurrency(curr)
  }, [])

  const handleSetLoading = useCallback((loading: boolean) => {
    setIsLoading(loading)
  }, [])

  const getCurrentPricing = useCallback(
    (billingCycle: BillingCycle = 'ANNUAL') => {
      if (currency === 'USD') {
        return billingCycle === 'ANNUAL'
          ? dynamicPricingUSDAnnual
          : dynamicPricingUSDMonthly
      }
      return billingCycle === 'ANNUAL'
        ? dynamicPricingINRAnnual
        : dynamicPricingINRMonthly
    },
    [
      currency,
      dynamicPricingUSDAnnual,
      dynamicPricingUSDMonthly,
      dynamicPricingINRAnnual,
      dynamicPricingINRMonthly,
    ]
  )

  const value = useMemo(
    () => ({
      currentPlanType,
      dynamicPricingUSDAnnual,
      dynamicPricingUSDMonthly,
      dynamicPricingINRAnnual,
      dynamicPricingINRMonthly,
      currency,
      isLoading,
      setCurrentPlanType: handleSetPlanType,
      setDynamicPricingUSDAnnual: handleSetDynamicPricingUSDAnnual,
      setDynamicPricingUSDMonthly: handleSetDynamicPricingUSDMonthly,
      setDynamicPricingINRAnnual: handleSetDynamicPricingINRAnnual,
      setDynamicPricingINRMonthly: handleSetDynamicPricingINRMonthly,
      setCurrency: handleSetCurrency,
      setIsLoading: handleSetLoading,
      getCurrentPricing,
    }),
    [
      currentPlanType,
      dynamicPricingUSDAnnual,
      dynamicPricingUSDMonthly,
      dynamicPricingINRAnnual,
      dynamicPricingINRMonthly,
      currency,
      isLoading,
      handleSetPlanType,
      handleSetDynamicPricingUSDAnnual,
      handleSetDynamicPricingUSDMonthly,
      handleSetDynamicPricingINRAnnual,
      handleSetDynamicPricingINRMonthly,
      handleSetCurrency,
      handleSetLoading,
      getCurrentPricing,
    ]
  )

  return (
    <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
  )
}

/**
 * Hook to access the pricing context
 *
 * @throws Error if used outside of PricingProvider
 *
 * @example
 * ```tsx
 * const { currentPlanType, dynamicPricing, setCurrentPlanType } = usePricingContext()
 * ```
 */
export function usePricingContext() {
  const context = useContext(PricingContext)

  if (context === undefined) {
    throw new Error('usePricingContext must be used within a PricingProvider')
  }

  return context
}

/**
 * Hook to check if a plan should be highlighted
 *
 * @param planType - The plan type to check
 * @returns Whether the plan should be highlighted
 *
 * @example
 * ```tsx
 * const isHighlighted = useIsPlanHighlighted('GROWTH')
 * ```
 */
export function useIsPlanHighlighted(planType: PlanType): boolean {
  const { currentPlanType } = usePricingContext()
  return currentPlanType === planType
}
